import type { ExperimentCreateLocation } from "@v2/features/infra/analytics/events";
import type { Experiment } from "schemas/generated/experiment";

import { TRPC_ERROR_CODES_BY_KEY } from "@trpc/server/unstable-core-do-not-import";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import { useSubscriptionTier } from "@v2/hooks/subscription";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { experimentRouter, experimentUtils } from "@v2/trpc/routes/experiment";
import { handleTRPCClientError } from "@v2/utils/error-handling";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";

type CreateExperimentProps = {
  location: ExperimentCreateLocation;
  partialExperiment?: Partial<Experiment>;
  onExperimentCreateSuccess?: () => void;
};

export const useExperimentCreate = ({
  workspaceId,
  projectId,
}: {
  workspaceId: string | undefined;
  projectId: string | undefined;
}) => {
  const logEvent = useLogAnalytics();
  const subscriptionTier = useSubscriptionTier();
  const navigate = useNavigate();
  const errorToast = useV1ErrorToast();

  const { mutateAsync: createDefault, isPending: isCreatingExperiment } =
    experimentRouter.createDefault.useMutation({
      meta: { reploIgnoreDefaultErrorHandling: true },
      onSuccess: async () => {
        await experimentUtils.list.invalidate();
      },
      onError: (error) => {
        if (error.shape?.code === TRPC_ERROR_CODES_BY_KEY.FORBIDDEN) {
          errorToast(
            "You don't have permission to create a test in this workspace.",
            "Error creating test",
            "error.experiment.create",
            {
              error: "No admin access to workspace",
              workspaceId,
              projectId,
            },
          );
        } else {
          handleTRPCClientError(error);
        }
      },
    });

  const createExperiment = async ({
    location,
    partialExperiment,
    onExperimentCreateSuccess,
  }: CreateExperimentProps) => {
    const createdExperiment = await createDefault({
      workspaceId,
      projectId,
      experiment: {
        ...partialExperiment,
      },
    });
    logEvent("experiment.create", {
      billingPlanTier: subscriptionTier,
      location,
    });

    let path: string;
    const createdExperimentId = createdExperiment.id;

    if (Boolean(projectId)) {
      path = generatePath(routes.shops.experiments.details, {
        projectId,
        experimentId: createdExperimentId,
      });
    } else {
      path = generatePath(routes.workspace.experiments.details, {
        workspaceId,
        experimentId: createdExperimentId,
      });
    }

    navigate(path, {
      state: { newExperiment: true },
    });
    onExperimentCreateSuccess?.();
  };

  return {
    createExperiment,
    isCreatingExperiment,
  };
};
